import SVGSprite from 'components/general/SVGSprite';
import { alpha } from 'foundations/theme';
import { Typography } from 'foundations/typography';
import React from 'react';
import styled from 'styled-components';
import { border, color, layout, space } from 'styled-system';

const noop = () => {};

const InputField = React.forwardRef((props, ref) => {
    const {
        onChange = noop,
        children,
        style = {},
        labelStyle = {},
        type = 'text',
        name = '',
        value = null,
        id = '',
        placeholder = '',
        required = false,
        labelPlaceholder = '',
        labelText = '',
        labelHtmlFor = '',
        showLimit = false,
        maxLength,
        floatLabel = false,
        state = 'normal',
        variant = '',
        errorMsg,
        onKeyDown,
        password = false,
        togglePassword = noop,
        icon,
        showLeftIcon = true,
        iconstyle,
        zIndexLabel,
        isMandatory = false,
        inlineCharCount = false,
        hideReqdError = false,
        onMouseUp,
        onDblClick,
        reqdErrorMsg = '  cannot be empty',
        isDisabled = false,
        clearInput = false,
        showClearOnValue = false,
        clearStyle = {},
        onClear = noop,
        wrapperStyle = {},
        charCountStyle = {},
        inputColor = 'ambience.5',
        wrapperClass = '',
        passwordShowLabel = 'Show/hide password',
        ariaLabel = 'This is an input field',
        socialAuthUser,
        ...rest
    } = props || {};

    const renderCharLimit = () => {
        return (
            <Typography
                variant={'subtext2'}
                color='ambience.0'
                style={{
                    padding: '3px 0 5px 0',
                }}
            >
                {value !== null ? value?.length || 0 : 0}/{maxLength}
            </Typography>
        );
    };

    const onValueChange = (e) => {
        onChange(e);
    };

    return (
        <>
            <InputWrapper
                mb={2}
                variant={variant}
                state={state}
                className={`input-wrapper ${wrapperClass}`}
                errorMsg={errorMsg}
                style={{ ...(wrapperStyle || {}) }}
            >
                {icon && showLeftIcon && <StyledSvgSprite icon={icon} />}
                <InputDom
                    ref={ref}
                    id={id}
                    type={type}
                    name={name}
                    required={required}
                    onChange={onValueChange}
                    style={{ ...style }}
                    placeholder={placeholder}
                    value={value}
                    maxLength={maxLength}
                    variant={variant}
                    state={state}
                    onKeyDown={onKeyDown}
                    {...rest}
                    color={inputColor}
                    floatLabel={floatLabel}
                    onDoubleClick={onDblClick}
                    onMouseUp={onMouseUp}
                    disabled={isDisabled}
                    aria-required={required ? true : false}
                    aria-label={ariaLabel}
                >
                    {children}
                </InputDom>

                <LabelDom
                    htmlFor={labelHtmlFor}
                    placeholder={labelPlaceholder}
                    variant={variant}
                    color='ambience.10'
                    floatLabel={floatLabel}
                    value={value}
                    labelStyle={{ ...labelStyle }}
                    zIndexLabel={zIndexLabel}
                    aria-label={
                        ariaLabel
                            ? ariaLabel
                            : `${labelText}${isMandatory ? '*' : ''}`
                    }
                >
                    {state === 'error' ? (
                        <InputError variant={variant}>
                            {required && !hideReqdError
                                ? value.length > 0
                                    ? errorMsg
                                    : socialAuthUser
                                    ? 'Password' + reqdErrorMsg
                                    : labelText + reqdErrorMsg
                                : errorMsg}
                        </InputError>
                    ) : (
                        `${labelText}${isMandatory ? '*' : ''}`
                    )}
                </LabelDom>
                {password && value && value.length > 0 && (
                    <ToggleShowPassword
                        onClick={togglePassword}
                        tabIndex={0}
                        role='button'
                        aria-label={passwordShowLabel}
                    >
                        <SVGSprite
                            style={iconstyle ? iconstyle : {}}
                            icon={icon}
                        />
                    </ToggleShowPassword>
                )}
                {maxLength && showLimit && inlineCharCount && (
                    <CharCountDom
                        isClearInput={clearInput}
                        className={'charCount'}
                    >
                        {renderCharLimit()}
                    </CharCountDom>
                )}
                {(clearInput || showClearOnValue) && (
                    <InputClear
                        onClick={onClear}
                        isDisabled={
                            showClearOnValue
                                ? value.length > 0
                                    ? false
                                    : true
                                : false
                        }
                        clearStyle={clearStyle}
                    >
                        <SVGIcon
                            icon={'icon-dismiss-cross'}
                            width={8}
                            height={8}
                        />
                    </InputClear>
                )}
            </InputWrapper>
            {maxLength && showLimit && !inlineCharCount && (
                <CharCountDom
                    className={'CharCountbox'}
                    style={{ ...charCountStyle }}
                >
                    {renderCharLimit()}
                </CharCountDom>
            )}
        </>
    );
});

export default InputField;

const SVGIcon = styled(SVGSprite)`
    fill: ${({ theme }) => alpha(theme.colors.white, 1)};
`;

const InputWrapper = styled.div`
    ${layout}
    ${space}
    ${color}
    ${border}
    width: 100%;
    position: relative;
    background: ${({ theme }) =>
        theme.isLightTheme ? 'transparent' : theme.colors.ambience[21]};
    border: 1px solid transparent;
    ${({ theme }) =>
        theme.isLightTheme &&
        `
        border: 1px solid ${theme.colors.ambience[21]};
    `};
    border-radius: 8px;
    font-family: ${({ theme }) => theme.fonts.book};
    ${({ variant, theme }) =>
        variant === 'light' &&
        `
        margin: 2px 0;
        background: ${theme.colors.ambience[0]};
    `}
    ${(props) =>
        props.variant === 'nolabel' &&
        props.state !== 'error' &&
        `
        margin: 0;
    `}
    ${(props) =>
        (props.variant === 'nolabel' || props.variant === 'hasIcon') &&
        props.state === 'error' &&
        `
        border-color: ${props.theme.colors.semantic[1]};
    `}
    ${(props) =>
        props.errorMsg &&
        `
        border-color: ${props.theme.colors.semantic[1]} !important;
    `}
`;
const InputDom = styled.input`
    ${layout}
    ${space}
    ${color}
    ${border}
    width: 100%;
    outline: none;
    padding: 10px 15px;
    font-style: normal;
    font-size: 16px;
    line-height: 1.2;
    height: 60px;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    transition: 0.2s ease-in-out;
    position: relative;
    z-index: 1;
    box-sizing: border-box;
    font-family: ${({ theme }) => theme.fonts.book};
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: textfield;
    background: ${({ theme }) =>
        theme.isLightTheme ? 'transparent' : theme.colors.ambience[21]};
    &:focus + label,
    &:valid + label {
        font-size: 12px;
        top: 0;
        background: transparent;
        color: ${({ theme }) => theme.colors.ambience[13]};
        transition: 0.2s ease-in-out;
    }
    &:focus ~ .charCount,
    &:valid ~ .charCount {
        top: 20px;
        transition: 0.2s ease-in-out;
    }
    ${({ theme, value }) =>
        value !== '' &&
        `
        padding-top: 22px;
        padding-bottom: 8px;
        + label {
            font-size: 12px;
            top: 0;
            background: transparent;
            color: ${theme.colors.ambience[13]};
            transition: 0.2s ease-in-out;
        }
        ~ .charCount {
            top: 20px;
            transition: 0.2s ease-in-out;
        }
    `}

    &:focus,
    &:valid {
        padding-top: 22px;
        padding-bottom: 8px;
    }

    ${({ theme, variant }) =>
        variant === 'light' &&
        `
        box-sizing: border-box;
        background: transparent;
        color: ${theme.colors.ambience[18]};
        height: 54px;
        &:focus{
            color: ${theme.colors.ambience[18]};
        }
    `}
    ${({ variant, theme }) =>
        (variant === 'clear' ||
            variant === 'nolabel' ||
            variant === 'hasIcon') &&
        `
        background: transparent;
        &:focus{
            background: ${
                theme.isLightTheme ? 'transparent' : theme.colors.ambience[22]
            };
        }
    `}
    ${(props) =>
        props.variant === 'nolabel' &&
        props.state !== 'error' &&
        `
        padding-top: 8px;
        
        &:focus,
        &:valid{
            padding-top: 8px;
        }
    `}
    &::placeholder {
        color: ${(props) => props.theme.colors.ambience[13]};
    }

    ${(props) =>
        props.variant === 'hasIcon' &&
        `
        padding-top: 8px;
        padding-left: 56px;
        
        &:focus,
        &:valid{
            padding-top: 8px;
        }
    `}

    ${({ state, theme }) =>
        state === 'empty' &&
        `
            color: ${theme.colors.ambience[6]};
    `}
    ${(props) =>
        (props.state === 'loading' || props.state === 'success') &&
        `
            pointer-events: none;
    `}
    &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0px 1000px
            ${({ theme }) =>
                theme.isLightTheme
                    ? theme.colors.white
                    : theme.colors.ambience[21]}
            inset;
        -webkit-text-fill-color: ${({ theme }) =>
            theme.colors.ambience[5]} !important;
        border: ${({ theme }) =>
            theme.isLightTheme
                ? 'none'
                : `1px solid ${theme.colors.ambience[21]}`};
        caret-color: ${({ theme }) =>
            theme.isLightTheme ? theme.colors.black : theme.colors.white};
        transition: background-color 5000s ease-in-out 5000s;
    }
    ${({ floatLabel, theme }) =>
        floatLabel === true &&
        `   
            padding-top: 22px;
            padding-bottom: 8px;
            &:focus + label, &:valid + label{
                top: 5px;
                color: ${theme.colors.ambience[0]};
            }
    `}
    &.input-mobile-large {
        @media (max-width: 470px) {
            height: 95px;
            padding-top: 35px;
        }
    }
    ${({ hideInputArrows }) =>
        hideInputArrows &&
        `   
            /* Chrome, Safari, Edge, Opera */
            &::-webkit-outer-spin-button,
            &::-webkit-inner-spin-button {
                -webkit-appearance: none;
                margin: 0;
            }

            /* Firefox */
            &[type=number] {
                -moz-appearance: textfield;
            }
    `}
`;

const InputError = styled.span`
    color: ${({ theme }) => theme.colors.semantic[1]};
    ${(props) =>
        props.variant === 'hasIcon' &&
        `
            position: absolute;
            left: 56px;
            top: 5px;
    `}
`;

const LabelDom = styled.label`
    ${layout}
    ${space}
    ${color}
    ${border}
    position: absolute;
    left: 0;
    text-align: left;
    transition: 0.2s ease-in-out;
    font-weight: normal;
    font-size: 16px;
    pointer-events: none;
    ${(props) => `
        z-index: ${props.zIndexLabel ? props.zIndexLabel : 0};	
    `}

    cursor: text;
    ${({ theme, variant }) =>
        variant === 'light' &&
        `	
        color: ${theme.colors.ambience[13]};	
    `}
    font-family: 'NotoSans-Regular';
    top: 8px;
    bottom: 0;
    padding: 8px 15px;
    margin-bottom: 13px;
    width: 100%;
    word-break: break-word;

    ${(props) => props.labelStyle && props.labelStyle}
    ${({ theme, floatLabel }) =>
        floatLabel === true &&
        `   
            font-size: 12px;
            top: 5px;
            left: 15px;
            z-index: 1;
            padding: 0px;
            background: transparent;
            color: ${theme.colors.ambience[0]};
            transition: 0.2s ease-in-out;
    `}
`;

const ToggleShowPassword = styled.span`
    position: absolute;
    top: 20px;
    right: 10px;
    z-index: 1;
    cursor: pointer;
    .icon {
        width: 24px;
        height: 24px;
        fill: ${({ theme }) => theme.colors.ambience[13]};
    }
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: solid;
    }
`;

const CharCountDom = styled.div`
    ${layout}
    ${space}
    ${color}
    ${border}
    font-family: 'NotoSans-Regular';
    justify-content: flex-end;
    display: flex;
    z-index: 0;
    cursor: text;
    ${({ theme, variant }) =>
        variant === 'light' &&
        `
        color: ${theme.colors.ambience[13]};
    `}
    font-family: 'NotoSans-Regular';
    top: 8px;
    bottom: 0;
    padding: 0 15px 8px 15px;
    margin: -10px 0 13px;
    width: 100%;
    &.charCount {
        pointer-events: none;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: ${({ isClearInput }) => (isClearInput ? '56px' : '10px')};
        z-index: 2;
        margin: 0;
        padding: 0;
        bottom: inherit;
        p {
            padding: 0;
        }
    }
`;

const StyledSvgSprite = styled(SVGSprite)`
    width: 20px;
    height: 20px;
    fill: ${({ theme, icon }) =>
        theme.isLightTheme
            ? icon === 'search-icon'
                ? theme.colors.ambience[9]
                : theme.colors.ambience[10]
            : icon === 'search-icon'
            ? theme.colors.ambience[13]
            : theme.colors.ambience[3]};
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    left: 17px;
    z-index: 2;
    &:hover {
        fill: ${({ theme, icon }) =>
            theme.isLightTheme
                ? icon === 'search-icon'
                    ? theme.colors.ambience[9]
                    : theme.colors.ambience[10]
                : icon === 'search-icon'
                ? theme.colors.ambience[13]
                : theme.colors.ambience[3]};
    }
`;

const InputClear = styled.div`
    width: 24px;
    height: 24px;
    background: ${({ theme }) => alpha(theme.colors.ambience[23], 0.64)};
    border-radius: 100%;
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 1;
    cursor: pointer;
    text-align: center;
    line-height: 22px;
    display: ${({ isDisabled }) => (isDisabled ? 'none' : 'block')};

    ${({ clearStyle }) => clearStyle && clearStyle}
`;
